import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, OrgRole } from '../context/AuthContext';
import { useI18n } from '../i18n';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredAccess?: 'wattelzce' | 'localizce';
  /** If provided, the user must have one of these roles in the current org (super_admin bypass). */
  requiredOrgRoles?: OrgRole[];
  /** If true, super_admin platform users are denied (e.g. org-only features). */
  denySuperAdmin?: boolean;
  /** If true, ONLY super_admin platform users are allowed (e.g. OTA). */
  requireSuperAdmin?: boolean;
}

/**
 * ProtectedRoute component that wraps routes requiring authentication.
 * Optionally checks for specific resource access (wattelzce or localizce).
 */
export default function ProtectedRoute({ children, requiredAccess, requiredOrgRoles, denySuperAdmin, requireSuperAdmin }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAccess, hasOrgRole, user } = useAuth();
  const location = useLocation();
  const { t } = useI18n();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('eq_loading')}</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check resource-specific access
  if (requiredAccess && !hasAccess(requiredAccess)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Deny platform super_admin (used for strictly org-level pages like Membres)
  if (denySuperAdmin && user?.platformRole === 'super_admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Allow ONLY super_admin (used for platform-only pages like OTA)
  if (requireSuperAdmin && user?.platformRole !== 'super_admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check org-role-based access (e.g. management features hidden from simple 'user')
  if (requiredOrgRoles && requiredOrgRoles.length > 0 && !hasOrgRole(...requiredOrgRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
